import { useApplication } from "@/api/application/application";
import {
  Box,
  Button,
  Link as ChakraLink,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  ListItem,
  OrderedList,
  Spinner,
  Stack,
  Tag,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import React from "react";
import { PiArrowUpRight } from "react-icons/pi";
import { Link } from "react-router-dom";
import { stepCompletion } from "../Registry/validation";
import { IoPersonCircleOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "../i18n/LocalizedLink";
import { useLocalePrefix } from "@/hooks/useLocalizedNavigate";
import { formatDateTime } from "@/utils/dateFormat";

export const AuthedHome = () => {
  const { data: application, isLoading } = useApplication();
  const { t } = useTranslation("home");
  const localePrefix = useLocalePrefix();

  const {
    general: generalComplete,
    consent: consentComplete,
    medical: medicalComplete,
    submitted: applicationSubmitted,
  } = stepCompletion(application);
  const canEditMedical = generalComplete && consentComplete;

  const APPLICATION_STATUS_MAP = {
    pending: {
      title: t("common:status.pendingReview"),
      colorScheme: "gray",
    },
    approved: {
      title: t("common:status.potentialPrimaCandidate"),
      colorScheme: "green",
    },
    rejected: {
      title: t("common:status.notCurrentlyEligible"),
      colorScheme: "red",
    },
  };

  const isProxy = application?.applicant_type === "proxy";
  const patientName = `${application?.patient_first_name} ${application?.patient_last_name}`;

  if (isLoading) return <Spinner />;

  return (
    <Grid
      padding={[2, 2, 4, 8]}
      templateColumns={["1fr", "1fr", "0.382fr 0.618fr"]}
      gap={[4, 4, 8, 16]}
      alignItems="flex-start"
    >
      <Stack spacing={8}>
        <Heading size="xl" lineHeight={1.2} fontWeight="normal">
          {t("authed.welcome")}
        </Heading>
        <Text fontSize="lg">
          {t("authed.description")}{" "}
          <ChakraLink
            color="blue.600"
            fontWeight="medium"
            textDecoration="underline"
            href="mailto:support@science.xyz"
          >
            {t("authed.emailUs")}
          </ChakraLink>{" "}
          {t("authed.or")}{" "}
          <ChakraLink
            color="blue.600"
            fontWeight="medium"
            href="https://science.xyz/company/contact/intake/"
            textDecoration="underline"
          >
            {t("authed.contactForm")}
          </ChakraLink>
          {t("authed.contactSuffix")}
        </Text>
      </Stack>

      <Box
        borderRadius="lg"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        marginBlock="0 auto"
        as="section"
        aria-labelledby="application-steps-heading"
      >
        <Flex
          padding={8}
          paddingBlockEnd={4}
          alignItems="center"
          justifyContent="space-between"
          flexDirection="column"
          backgroundColor="gray.50"
          gap={4}
          textAlign="center"
        >
          {!applicationSubmitted && (
            <>
              <Heading
                size="lg"
                id="application-steps-heading"
                textAlign="center"
                fontSize="2xl"
                fontWeight="bold"
                color="black"
                lineHeight="shorter"
                width="full"
                paddingBlockEnd={4}
                style={{ textWrap: "balance" }}
              >
                {t("authed.notSubmittedHeading")}
              </Heading>

              <Text
                fontSize="xl"
                lineHeight="short"
                paddingBlockEnd={6}
                style={{ textWrap: "balance" }}
              >
                {t("authed.notSubmittedDescription")}
              </Text>
              <OrderedList
                m={0}
                paddingBlockStart={4}
                width="full"
                display="flex"
                flexDirection="column"
                gap={4}
              >
                <ListItem
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  margin={0}
                  alignItems={["start", "start", "start", "center"]}
                  flexDirection={["column", "column", "column", "row"]}
                  paddingBlockEnd={4}
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                  fontSize="lg"
                >
                  <Button
                    as={LocalizedLink}
                    to="/registry"
                    colorScheme={generalComplete ? "black" : "blue"}
                    size={generalComplete ? "md" : "lg"}
                    variant={generalComplete ? "link" : undefined}
                    width={["full", "full", "full", "auto"]}
                    rightIcon={<Icon as={PiArrowUpRight} />}
                    whiteSpace="wrap"
                    justifyContent="start"
                    height="fit-content"
                    textAlign="start"
                    paddingBlock={generalComplete ? 0 : 4}
                  >
                    {generalComplete ? t("authed.stepGeneralReview") : t("authed.stepGeneral")}
                  </Button>
                </ListItem>
                <ListItem
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  margin={0}
                  alignItems={["start", "start", "start", "center"]}
                  flexDirection={["column", "column", "column", "row"]}
                  paddingBlockEnd={4}
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                  fontSize="md"
                >
                  <Button
                    as={LocalizedLink}
                    to="/registry/consent"
                    colorScheme={generalComplete && !consentComplete ? "blue" : "black"}
                    size={generalComplete && !consentComplete ? "lg" : "md"}
                    variant={generalComplete && !consentComplete ? undefined : "link"}
                    width={["full", "full", "full", "auto"]}
                    rightIcon={<Icon as={PiArrowUpRight} />}
                    whiteSpace="wrap"
                    justifyContent="start"
                    height="fit-content"
                    textAlign="start"
                    paddingBlock={generalComplete && !consentComplete ? 4 : 0}
                  >
                    {consentComplete ? t("authed.stepConsentReview") : t("authed.stepConsent")}
                  </Button>
                </ListItem>
                <ListItem
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  margin={0}
                  alignItems={"start"}
                  flexDirection={"column"}
                  paddingBlockEnd={4}
                  display="flex"
                  justifyContent="space-between"
                  gap={4}
                  fontSize="lg"
                >
                  <Button
                    as={LocalizedLink}
                    to="/registry/medical"
                    colorScheme={canEditMedical && !medicalComplete ? "blue" : "black"}
                    size={canEditMedical && !medicalComplete ? "lg" : "md"}
                    variant={canEditMedical && !medicalComplete ? "solid" : "link"}
                    width={["full", "full", "full", "auto"]}
                    rightIcon={<Icon as={PiArrowUpRight} />}
                    whiteSpace="wrap"
                    justifyContent="start"
                    height="fit-content"
                    textAlign="start"
                    paddingBlock={canEditMedical && !medicalComplete ? 4 : 0}
                  >
                    {medicalComplete ? t("authed.stepMedicalReview") : t("authed.stepMedical")}
                  </Button>
                </ListItem>
                <ListItem
                  margin={0}
                  alignItems={["start", "start", "start"]}
                  flexDirection={["column", "column", "column", "row"]}
                  paddingBlockEnd={4}
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                  fontSize="lg"
                >
                  <Button
                    as={LocalizedLink}
                    to="/registry/confirm"
                    colorScheme={medicalComplete ? "blue" : "black"}
                    size={medicalComplete ? "lg" : "md"}
                    variant={medicalComplete ? undefined : "link"}
                    width={["full", "full", "full", "auto"]}
                    rightIcon={<Icon as={PiArrowUpRight} />}
                    whiteSpace="wrap"
                    justifyContent="start"
                    height="fit-content"
                    textAlign="start"
                    paddingBlock={medicalComplete ? 4 : 0}
                  >
                    {t("authed.stepConfirm")}
                  </Button>
                </ListItem>
              </OrderedList>
            </>
          )}

          {applicationSubmitted && (
            <Stack spacing={0} marginBlockEnd={2} textAlign="left">
              <HStack spacing={2} paddingBlockEnd={4}>
                <Icon as={IoPersonCircleOutline} boxSize={20} padding={1} />
                <Heading
                  data-testid="submission-date"
                  fontSize="2xl"
                  fontWeight="bold"
                  color="black"
                  lineHeight="shorter"
                  width="full"
                  style={{ textWrap: "balance" }}
                >
                  <Text as="span" fontWeight="medium" display="block" fontSize="lg">
                    {t("authed.registrationFor")}
                  </Text>
                  {application?.patient_first_name} {application?.patient_last_name}
                </Heading>
              </HStack>
              <Stack backgroundColor="white" padding={6} gap={2} borderRadius="lg" marginBlock={4}>
                <Heading fontSize="lg" fontWeight="bold">
                  {t("authed.applicationStatus")}
                </Heading>
                <Tag
                  colorScheme="gray"
                  size="lg"
                  fontWeight="bold"
                  fontSize="lg"
                  paddingBlock={4}
                  paddingInline={6}
                  width="full"
                  variant="subtle"
                  marginBlockEnd={4}
                >
                  {APPLICATION_STATUS_MAP[application?.status ?? "pending"].title}
                </Tag>
                {application?.status === "pending" && (
                  <>
                    <Text fontSize="lg">
                      {t("authed.pendingMessage", {
                        forPatient: isProxy
                          ? t("authed.forPatient", {
                              firstName: application?.patient_first_name,
                              lastName: application?.patient_last_name,
                            })
                          : "",
                      })}
                    </Text>
                    <Text paddingBlockStart={4} fontSize="lg">
                      <Text as="span" display="block" fontWeight="bold">
                        {t("authed.applicationLastUpdated")}
                      </Text>
                      {formatDateTime(application?.updated_at)}
                    </Text>
                  </>
                )}
                {application?.status === "approved" && (
                  <Text fontSize="lg">
                    {isProxy
                      ? t("authed.approvedMessage_proxy", { name: patientName })
                      : t("authed.approvedMessage_self")}
                  </Text>
                )}
                {application?.status === "rejected" && (
                  <Text fontSize="lg">
                    {isProxy
                      ? t("authed.rejectedMessage_proxy", { name: patientName })
                      : t("authed.rejectedMessage_self")}
                  </Text>
                )}
              </Stack>
              <Heading marginBlockStart={4} fontSize="lg" paddingBlockEnd={2}>
                {t("authed.nextSteps")}
              </Heading>

              {application?.status === "pending" && (
                <Stack spacing={4}>
                  <Text fontSize="lg">
                    {isProxy
                      ? t("authed.pendingNextSteps1_proxy")
                      : t("authed.pendingNextSteps1_self")}
                  </Text>
                  <Text fontSize="lg">
                    {isProxy
                      ? t("authed.pendingNextSteps2_proxy")
                      : t("authed.pendingNextSteps2_self")}
                  </Text>
                </Stack>
              )}

              {application?.status === "approved" && (
                <Stack gap={4}>
                  <Text fontSize="lg">{t("authed.approvedNextSteps1")}</Text>
                  <Text fontSize="lg">
                    {isProxy
                      ? t("authed.approvedNextSteps2_proxy")
                      : t("authed.approvedNextSteps2_self")}
                  </Text>
                </Stack>
              )}

              {application?.status === "rejected" && (
                <Stack gap={4}>
                  <Text fontSize="lg">{t("authed.rejectedNextSteps1")}</Text>
                  <Text fontSize="lg">{t("authed.rejectedNextSteps2")}</Text>
                  <Text fontSize="lg">
                    {isProxy
                      ? t("authed.rejectedNextSteps3_proxy")
                      : t("authed.rejectedNextSteps3_self")}
                  </Text>
                </Stack>
              )}

              <Button
                as={LocalizedLink}
                to="/registry/medical"
                colorScheme="blue"
                backgroundColor="white"
                size="lg"
                variant="outline"
                width={["full", "full", "full", "auto"]}
                rightIcon={<Icon as={PiArrowUpRight} />}
                marginBlock={4}
              >
                {t("common:buttons.updateMedicalInfo")}
              </Button>
              <details>
                <Text as="summary" fontSize="lg" fontWeight="medium">
                  {t("authed.reviewSubmittedApplication")}
                </Text>

                <Text paddingBlockStart={4} fontSize="lg">
                  <Text as="span" display="block" fontWeight="bold">
                    {t("authed.applicationLastUpdated")}
                  </Text>
                  {formatDateTime(application?.updated_at)}
                </Text>

                <UnorderedList
                  listStyleType="none"
                  margin={0}
                  paddingBlock={4}
                  gap={2}
                  display="flex"
                  flexDirection="column"
                >
                  <ListItem>
                    <Button
                      size="lg"
                      fontWeight="medium"
                      as={LocalizedLink}
                      variant="link"
                      to="/registry"
                      colorScheme="blue"
                    >
                      {t("authed.generalInformation")}{" "}
                      <Icon as={PiArrowUpRight} marginInlineStart={1} />
                    </Button>
                  </ListItem>
                  <ListItem>
                    <Button
                      size="lg"
                      fontWeight="medium"
                      as={LocalizedLink}
                      variant="link"
                      to="/registry/consent"
                      colorScheme="blue"
                    >
                      {t("authed.consentAgreement")}{" "}
                      <Icon as={PiArrowUpRight} marginInlineStart={1} />
                    </Button>
                  </ListItem>
                  <ListItem>
                    <Button
                      size="lg"
                      fontWeight="medium"
                      as={LocalizedLink}
                      variant="link"
                      to="/registry/confirm"
                      colorScheme="blue"
                    >
                      {t("authed.confirmationAndSubmission")}{" "}
                      <Icon as={PiArrowUpRight} marginInlineStart={1} />
                    </Button>
                  </ListItem>
                </UnorderedList>
              </details>
            </Stack>
          )}
        </Flex>
      </Box>
    </Grid>
  );
};
